import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileText,
  Atom,
  FolderOpen,
  Wrench,
  Upload,
  Pencil,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import apiClient from '../api/client';
import { StructureEditorModal } from '../components/chemistry/StructureEditor';
import { useUIStore } from '../stores/uiStore';
import { formatRelativeDate } from '../utils/dateFormat';

// ---------------------------------------------------------------------------
// Hub health definitions
// ---------------------------------------------------------------------------

interface HubDef {
  name: string;
  port: number;
  endpoint: string;
  method: 'get' | 'post';
  payload?: any;
}

const HUBS: HubDef[] = [
  { name: 'ADMET & Safety', port: 8082, endpoint: '/hub-health/admet', method: 'get' },
  { name: 'Molecular Analysis', port: 8090, endpoint: '/hub-health/rdkit', method: 'get' },
  { name: 'Metabolism', port: 8089, endpoint: '/hub-health/metabolism', method: 'get' },
  { name: 'Library / Combi', port: 8083, endpoint: '/hub-health/library', method: 'get' },
  { name: 'Genomics', port: 8087, endpoint: '/hub-health/genomics', method: 'get' },
  { name: 'PK/PD Modelling', port: 8084, endpoint: '/hub-health/pkpd', method: 'get' },
  { name: 'GenBio / Protein', port: 8093, endpoint: '/hub-health/genbio', method: 'get' },
  { name: 'Equation Solver', port: 8095, endpoint: '/hub-health/calcs', method: 'get' },
  { name: 'ELN Hub', port: 18080, endpoint: '/hub-health/eln', method: 'get' },
];

interface HubStatus {
  name: string;
  port: number;
  status: 'checking' | 'healthy' | 'down';
  ms: number | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const openUploadDialog = useUIStore((s) => s.openUploadDialog);

  // Stats
  const [fileCount, setFileCount] = useState<number | null>(null);
  const [projectCount, setProjectCount] = useState<number | null>(null);
  const [moleculeCount, setMoleculeCount] = useState<number | null>(null);

  // Hub health
  const [hubStatuses, setHubStatuses] = useState<HubStatus[]>(
    HUBS.map(h => ({ name: h.name, port: h.port, status: 'checking', ms: null }))
  );
  const [expandedHub, setExpandedHub] = useState<string | null>(null);

  // Recent files
  const [recentFiles, setRecentFiles] = useState<any[]>([]);

  // Structure editor
  const [showEditor, setShowEditor] = useState(false);

  // Fetch stats
  useEffect(() => {
    apiClient.get('/projects/summary').then(r => {
      setProjectCount(r.data.projectCount ?? 0);
      setMoleculeCount(r.data.uniqueCompoundCount ?? 0);
      setFileCount(r.data.chemistryFileCount ?? 0);
    }).catch(() => { setProjectCount(0); setMoleculeCount(0); setFileCount(0); });
  }, []);

  // Fetch recent files
  useEffect(() => {
    apiClient.get('/files', { params: { limit: 5, sort: '-updated_at' } }).then(r => {
      const data = Array.isArray(r.data) ? r.data : r.data?.items || r.data?.files || [];
      setRecentFiles(data.slice(0, 5));
    }).catch(() => {});
  }, []);

  // Hub health checks
  const checkHubs = () => {
    setHubStatuses(HUBS.map(h => ({ name: h.name, port: h.port, status: 'checking', ms: null })));
    HUBS.forEach((hub, idx) => {
      const t0 = Date.now();
      const req = hub.method === 'post'
        ? axios.post(hub.endpoint, hub.payload, { timeout: 5000 })
        : axios.get(hub.endpoint, { timeout: 5000 });
      req.then((response) => {
        const contentType = String(response.headers?.['content-type'] || '').toLowerCase();
        const isHtml = contentType.includes('text/html') || typeof response.data === 'string';
        if (isHtml || !contentType.includes('json')) throw new Error('Invalid health response');
        setHubStatuses(prev => {
          const next = [...prev];
          next[idx] = { ...next[idx], status: 'healthy', ms: Date.now() - t0 };
          return next;
        });
      }).catch((err: any) => {
        setHubStatuses(prev => {
          const next = [...prev];
          next[idx] = { ...next[idx], status: 'down', ms: null };
          return next;
        });
      });
    });
  };

  useEffect(() => { checkHubs(); }, []);

  const healthyCount = hubStatuses.filter(h => h.status === 'healthy').length;
  const downCount = hubStatuses.filter(h => h.status === 'down').length;
  const isAdmin = user?.role === 'admin' || user?.user_type === 'admin' || user?.user_type === 'super_admin';
  const firstName = user?.first_name || user?.username || user?.email?.split('@')[0] || 'User';

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Welcome header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text-primary)' }}>
          Welcome back, {firstName}
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: '4px 0 0' }}>
          Adroit Chemistry Platform — overview and quick access
        </p>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 28 }}>
        <StatCard icon={<FileText size={20} color="#3b82f6" />} label="Chemistry Files" value={fileCount != null ? String(fileCount) : '...'} bg="rgba(59,130,246,0.08)" to="/chemistry/files" />
        <StatCard icon={<Atom size={20} color="#10b981" />} label="Compounds" value={moleculeCount != null ? moleculeCount.toLocaleString() : '...'} bg="rgba(16,185,129,0.08)" to="/chemistry" />
        <StatCard icon={<FolderOpen size={20} color="#f59e0b" />} label="Projects" value={projectCount != null ? String(projectCount) : '...'} bg="rgba(245,158,11,0.08)" to="/chemistry/projects" />
        <StatCard icon={<Wrench size={20} color="#8b5cf6" />} label="Active Tools" value="59" bg="rgba(139,92,246,0.08)" to="/hubs" />
      </div>

      {/* Hub Health */}
      <div style={{ marginBottom: 28 }}>
        {isAdmin ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>Hub Health</h2>
                <span style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 6, fontWeight: 600, fontFamily: 'monospace',
                  background: healthyCount === HUBS.length ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                  color: healthyCount === HUBS.length ? '#10b981' : '#f59e0b',
                }}>
                  {healthyCount}/{HUBS.length} online
                </span>
              </div>
              <button onClick={checkHubs} style={refreshBtn}>
                <RefreshCw size={13} /> Refresh
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
              {hubStatuses.map(h => (
                <div
                  key={h.name}
                  style={{ ...hubCard, cursor: 'pointer', transition: 'border-color 200ms ease' }}
                  onClick={() => setExpandedHub(expandedHub === h.name ? null : h.name)}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = '#14b8a6'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--card-border)'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                      background: h.status === 'healthy' ? '#10b981' : h.status === 'down' ? '#ef4444' : '#f59e0b',
                      boxShadow: h.status === 'healthy' ? '0 0 6px rgba(16,185,129,0.4)' : 'none',
                    }} />
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{h.name}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
                    <span style={{
                      fontSize: 11, fontFamily: 'monospace',
                      color: h.status === 'healthy' ? '#10b981' : h.status === 'down' ? '#ef4444' : '#f59e0b',
                    }}>
                      {h.status === 'checking' ? 'pinging...' : h.status === 'healthy' ? `${h.ms}ms` : 'offline'}
                    </span>
                  </div>
                  {expandedHub === h.name && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--card-border)', fontSize: 11 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ color: 'var(--text-muted)' }}>Hub</span>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{h.name}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ color: 'var(--text-muted)' }}>Status</span>
                        <span style={{ color: h.status === 'healthy' ? '#10b981' : h.status === 'down' ? '#ef4444' : '#f59e0b', fontWeight: 600 }}>{h.status === 'healthy' ? 'Online' : h.status === 'down' ? 'Offline' : 'Checking'}</span>
                      </div>
                      {h.ms != null && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                          <span style={{ color: 'var(--text-muted)' }}>Latency</span>
                          <span style={{ fontFamily: 'monospace', color: 'var(--text-primary)' }}>{h.ms}ms</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--text-muted)' }}>Last checked</span>
                        <span style={{ color: 'var(--text-primary)' }}>{new Date().toLocaleTimeString()}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '12px 18px',
            background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12,
          }}>
            <span style={{ fontSize: 15 }}>
              {downCount === 0 ? '\u2705' : '\u26a0\ufe0f'}
            </span>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
              {downCount === 0
                ? 'All systems operational'
                : `${downCount} service${downCount !== 1 ? 's' : ''} degraded`}
            </span>
          </div>
        )}
      </div>

      {/* Recent Files */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>Recent Files</h2>
        {recentFiles.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13,
            background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12 }}>
            No files yet. Upload one to get started.
          </div>
        ) : (
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12, overflow: 'hidden' }}>
            {recentFiles.map((f: any, i: number) => (
              <Link
                key={f.id || i}
                to={`/chemistry/file/${f.id}/editor`}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 16px', cursor: 'pointer',
                  borderBottom: i < recentFiles.length - 1 ? '1px solid var(--card-border)' : 'none',
                  transition: 'background 150ms ease', textDecoration: 'none', color: 'inherit',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--hover-bg)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <FileText size={14} color="var(--text-secondary)" />
                  <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                    {f.originalFileName || f.original_filename || f.fileName || f.name || f.filename || `File #${f.id}`}
                  </span>
                  {f.format && (
                    <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--hover-bg)',
                      color: 'var(--text-muted)', fontFamily: 'monospace', fontWeight: 600 }}>
                      {f.format}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    {f.updated_at ? formatRelativeDate(f.updated_at) : ''}
                  </span>
                  <ArrowRight size={12} color="var(--text-muted)" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div>
        <h2 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>Quick Actions</h2>
        <div style={{ display: 'flex', gap: 12 }}>
          <ActionBtn icon={<Upload size={16} />} label="Upload File" onClick={() => openUploadDialog()} />
          <ActionBtn icon={<Pencil size={16} />} label="Draw Structure" onClick={() => setShowEditor(true)} />
          <ActionBtn icon={<Wrench size={16} />} label="Open Toolbox" to="/hubs" />
        </div>
      </div>

      {/* Structure Editor Modal */}
      <StructureEditorModal
        isOpen={showEditor}
        onClose={() => setShowEditor(false)}
        onSave={(_molfile, smiles) => {
          setShowEditor(false);
          navigate('/hubs', { state: { smiles } });
        }}
        initialStructure=""
        title="Draw Structure"
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: string; bg: string; to: string }> = ({ icon, label, value, bg, to }) => (
  <Link
    to={to}
    style={{
      background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 14,
      padding: '20px 18px', display: 'flex', alignItems: 'center', gap: 14,
      cursor: 'pointer', transition: 'border-color 200ms ease', textDecoration: 'none', color: 'inherit',
    }}
    onMouseEnter={e => { e.currentTarget.style.borderColor = '#14b8a6'; }}
    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; }}
  >
    <div style={{
      width: 44, height: 44, borderRadius: 12, background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{label}</div>
    </div>
  </Link>
);

const ActionBtn: React.FC<{ icon: React.ReactNode; label: string; onClick?: () => void; to?: string }> = ({ icon, label, onClick, to }) => {
  const style: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    padding: '10px 20px', borderRadius: 10,
    background: 'var(--card-bg)', border: '1px solid var(--card-border)',
    color: 'var(--text-primary)', fontSize: 13, fontWeight: 500,
    cursor: 'pointer', transition: 'all 200ms ease', textDecoration: 'none',
  };
  const events = {
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      e.currentTarget.style.borderColor = '#f59e0b';
      e.currentTarget.style.background = 'var(--hover-bg)';
    },
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
      e.currentTarget.style.borderColor = 'var(--card-border)';
      e.currentTarget.style.background = 'var(--card-bg)';
    },
  };
  if (to) return <Link to={to} style={style} {...events}>{icon} {label}</Link>;
  return <button onClick={onClick} style={style} {...events}>{icon} {label}</button>;
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const hubCard: React.CSSProperties = {
  background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10,
  padding: '10px 14px',
};

const refreshBtn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '4px 10px', borderRadius: 6,
  background: 'none', border: '1px solid var(--card-border)',
  color: 'var(--text-muted)', fontSize: 11, cursor: 'pointer',
};

export default DashboardPage;
